/**
 * SelectDropdown Component
 *
 * UI/Aesthetics: Searchable dropdown with custom trigger, search bar, and filterable options list
 * Functions:
 * - Displays selected option with custom display text vs full label
 * - Search functionality to filter options by label text
 * - Click outside to close, clear selection, toggle open/close
 * - Error states with highlighting, required field indicators
 * - Responsive width calculation based on longest display text
 * - Keyboard navigation and accessibility
 * Previous: Used MUI TextField + Popper + List with custom search and width calculations
 * Now: Uses shadcn Button + Popover + Command components for search and selection
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { CheckIcon, CaretDownIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { T } from 'gt-react';

export interface SelectDropdownOption {
  id: string;
  label: string; // Longer description for dropdown list
  display: string; // Short display text for main input
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
}

interface SelectDropdownProps {
  options: SelectDropdownOption[];
  value: SelectDropdownOption | null;
  onChange: (selected: SelectDropdownOption | null) => void;
  id?: string;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default' | 'small'; // 'small' for MUI compatibility
  error?: boolean;
  helperText?: string;
  required?: boolean;
  highlightError?: boolean;
  className?: string;
  disabledOptionIds?: string[]; // IDs of options that should be disabled
  disabledHelperText?: string; // Tooltip text for disabled options
  /** An action listed below the options whatever the search, such as "New …". */
  footerAction?: SelectDropdownFooterAction;
}

export interface SelectDropdownFooterAction {
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Runs after the dropdown closes. */
  onSelect: () => void;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  id,
  label,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search',
  disabled = false,
  fullWidth = true,
  size = 'default',
  error = false,
  helperText,
  required = false,
  highlightError = true,
  className,
  disabledOptionIds = [],
  disabledHelperText = 'This option is not available',
  footerAction,
}) => {
  const [open, setOpen] = useState(false);

  // Convert MUI size to shadcn size
  const getButtonSize = (): 'sm' | 'default' => {
    return size === 'small' ? 'sm' : (size as 'sm' | 'default') || 'default';
  };

  // Handle option selection
  const handleSelect = (option: SelectDropdownOption) => {
    onChange(option);
    setOpen(false);
  };

  // Compute target width from the longest DISPLAY text (not label) with a small buffer.
  const longestDisplayLength = options.reduce((max, option) => {
    const len = option.display ? option.display.length : 0;
    return len > max ? len : max;
  }, 0);
  const widthBufferCh = 2; // small breathing room
  // Account for end-adornments: chevron + right padding
  const chevronPx = 28;
  const rightPaddingPx = 12;
  const adornmentPx = chevronPx + rightPaddingPx;
  const computedWidthCss = `calc(${Math.max(
    0,
    longestDisplayLength + widthBufferCh,
  )}ch + ${adornmentPx}px)`;

  return (
    <div className={cn('space-y-2', fullWidth && 'w-full min-w-0', className)}>
      {label && (
        <Label
          className={cn('text-sm font-medium', error && 'text-destructive')}
        >
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}

      <div className="flex">
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              size={getButtonSize()}
              className={cn(
                'justify-between font-normal',
                !value && 'text-muted-foreground',
                fullWidth && 'min-w-0 w-full',
                error &&
                  highlightError &&
                  'border-destructive bg-destructive/5 ring-1 ring-destructive/20',
                className,
              )}
              style={{
                width: fullWidth ? computedWidthCss : undefined,
                maxWidth: fullWidth ? '100%' : undefined,
                minWidth: fullWidth ? undefined : 'fit-content',
              }}
            >
              <span className="min-w-0 flex-1 truncate text-left">
                {value ? value.display : placeholder}
              </span>
              <CaretDownIcon className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="p-0"
            style={{
              width: fullWidth ? computedWidthCss : undefined,
              minWidth: '200px',
              maxWidth: 'min(80vw, 600px)',
            }}
            align="start"
            // `avoidCollisions={true}` was here and is the default in both Radix
            // and Base UI, so it is dropped rather than translated. The padding
            // is the part that was doing work.
            collisionPadding={8}
          >
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList className="max-h-[300px]">
                <CommandEmpty>
                  <T context="dropdown empty state message when search yields no results">
                    No options found.
                  </T>
                </CommandEmpty>
                <CommandGroup>
                  {options.map((option) => {
                    const isDisabled = disabledOptionIds.includes(option.id);
                    return (
                      <CommandItem
                        key={option.id}
                        value={option.label}
                        onSelect={() => {
                          if (!isDisabled) {
                            handleSelect(option);
                          }
                        }}
                        disabled={isDisabled}
                        className={cn(
                          'min-h-[2.5rem] items-start py-2',
                          isDisabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer',
                        )}
                        title={isDisabled ? disabledHelperText : undefined}
                      >
                        <CheckIcon
                          className={cn(
                            'mr-2 h-4 w-4 mt-0.5 flex-shrink-0',
                            value?.id === option.id
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <span className="flex-1 whitespace-normal leading-tight">
                          {option.label}
                        </span>
                        {isDisabled && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            <T context="label indicating an option is already in use">
                              (in use)
                            </T>
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                {footerAction && (
                  <>
                    <CommandSeparator alwaysRender />
                    <CommandGroup forceMount>
                      <CommandItem
                        forceMount
                        value="__select-dropdown-footer-action__"
                        onSelect={() => {
                          setOpen(false);
                          footerAction.onSelect();
                        }}
                        className="cursor-pointer"
                      >
                        {footerAction.icon}
                        {footerAction.label}
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {helperText && (
        <p
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export default SelectDropdown;
